import { createRouter, createWebHistory } from 'vue-router'
import { useInterfaceStore } from '@/store/UiStore';
import { FlowGuard, FlowService } from '@/services/FlowService';
import { QualGuard } from '@/services/QualGuard';
import { StripQuery } from '@/helpers/StripQuery';
import { BasicAnalyticsService } from '@/services/BasicAnalyticsService';
import { FacebookPixelService } from '@/services/FacebookPixelService';
import { GoogleAnalyticsService } from '@/services/GoogleAnalyticsService';

import PassThrough from '@/views/PassThrough.vue';
import FetcherView from '@/views/FetcherView.vue';
import FormContactView from '@/views/FormContactView.vue';
import formCurrentInsulation from '@/views/FormCurrentInsulationView.vue';
import { LeadDataService } from '@/services/LeadDataService';
import { debounce } from 'lodash';
import { getInterestDefinition } from '@/rules/InterestDefinitions';
import { useLeadStore } from '@/store/LeadStore';
import { getCookieConsentQueryValue, getCookieConsentValue, hasCookieConsentDecision, hasCookieConsentQuery, setCookieConsent } from '@/helpers/CookieConsent';

const routes = [
  {
    path: '/:uuid?',
    name: 'home',
    component: PassThrough,
    redirect: (to) => {
      return { name: 'formContact', params: to.params, query: to.query };
    },

    children: [
      {
        path: 'fetch',
        name: 'fetcher',
        component: FetcherView,
      },
      {
        path: 'beller-loper',
        name: 'formBellerLoper',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormBellerLoperView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'contact-details',
        name: 'formContact',
        component: FormContactView,
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'saved',
        name: 'saved',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/SavedView.vue'
          ),
        meta: {
          analyticsViewType: 'saved',
        },
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'current-insulation',
        name: 'formCurrentInsulation',
        component: formCurrentInsulation,
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'address-details',
        name: 'formAddress',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormAddressView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'partner-coupon',
        name: 'formPartnerCoupon',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormPartnerCouponView.vue'
          ),
        beforeEnter: [
          QualGuard,
          FlowGuard,
          (to) => {
            let store = useInterfaceStore();
            if (store.skipFormPartnerCoupon) {
              return { name: FlowService.getNext(to.name), params: to.params };
            }
          },
        ],
      },
      {
        path: 'ownership',
        name: 'formOwnership',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormOwnershipView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      // {
      //   path: 'advice',
      //   name: 'formAdvice',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormAdviceView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      {
        path: 'contactPreferences',
        name: 'formContactPreferences',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormContactPreferencesView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      // {
      //   path: 'financingSnnSubsidy10k',
      //   name: 'formFinancingSnnSubsidy10k',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormFinancingSnnSubsidy10kView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      // {
      //   path: 'financingSnnSubsidy4k',
      //   name: 'formFinancingSnnSubsidy4k',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormFinancingSnnSubsidy4kView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },

      //Finance page
      {
        path: 'financingIncome',
        name: 'formFinancingIncome',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormFinancingIncomeView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'financingIncome-advies',
        name: 'formFinancingIncomeAdvies',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormFinancingIncomeView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'other-subsidies',
        name: 'otherSubsidies',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/OtherSubsidies.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'woz-waarde',
        name: 'formWozWaarde',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormWozWaardeView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'subsidie-energielabel',
        name: 'formSubsidyEnergyLabel',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormSubsidyEnergyLabelView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      // {
      //   path: 'subsidie-vhf',
      //   name: 'formSubsidyVhf',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormSubsidyVhfView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      {
        path: 'subsidie',
        name: 'formSubsidy',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormSubsidyView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'other-subsidies-final', //TODO remove this one, this is just temporary
        name: 'otherSubsidiesFinal',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/OtherSubsidiesFinal.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'snnSubsidies',
        name: 'snnSubsidies',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormSNNSubsidiesView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      
      {
        path: 'intake-end',
        name: 'intakeEnd',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/IntakeEndView.vue'
          ),
        meta: {
          analyticsViewType: 'completed',
        },
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'financingNip',
        name: 'formFinancingNip',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormFinancingNipView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'financingVhf',
        name: 'formFinancingVhf',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormFinancingVhfView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      // {
      //   path: 'financingNip',
      //   name: 'formFinancingNip',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormFinancingNipView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      // {
      //   path: 'financingMaatregel29',
      //   name: 'formFinancingMaatregel29',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormFinancingMaatregel29View.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      // {
      //   path: 'financingWarmtefonds',
      //   name: 'formFinancingWarmtefonds',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormFinancingWarmtefondsView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      // {
      //   path: 'financingStimuleringslening',
      //   name: 'formFinancingStimuleringslening',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormFinancingStimuleringsLeningView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      // {
      //   path: 'financingMortgage',
      //   name: 'formFinancingMortgage',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormFinancingMortgageView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      // {
      //   path: 'practicalHelp',
      //   name: 'formPracticalHelp',
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "formViews" */ '../views/FormPracticalHelpView.vue'
      //     ),
      //   beforeEnter: [QualGuard, FlowGuard],
      // },
      {
        path: 'property',
        name: 'formProperty',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormPropertyView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      //Property just for advies
      {
        path: 'property-advies',
        name: 'formPropertyAdvies',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormPropertyView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },

      //TWK
      {
        path: 'terugwerkende-kracht',
        name: 'formTwk',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormTwkView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'help-met-financiering',
        name : 'formHelpFinancing',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormHelpFinancing.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      //TWK only for advies
      {
        path: 'terugwerkende-kracht-advies',
        name: 'formTwkAdvies',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormTwkView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        'path': 'isolatieplan-check',
        'name': 'isolatieplanCheck', 
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormIsolatieplanCheckView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        'path': 'isolatieaanpak',
        'name': 'isolatieaanpak', 
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormAanpakView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'isolatieaanpak-ondersteuning',
        name: 'formIsolatieaanpakSupport',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormIsolatieaanpakSupportView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        'path': 'overview',
        'name': 'overview', 
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/OverviewView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'executed-measures',
        name: 'formExecutedMeasures',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormExecutedMeasuresView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'twk-uitgebreid',
        name: 'formTwkUitgebreid',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormTwkUitgebreidView.vue'
          ),
      beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'intentions',
        name: 'formIntentions',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormIntentionsView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'interests',
        name: 'formInterests',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormInterestsView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'wishes',
        name: 'formWishes',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormWishesView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'advies-isolatieplan-interest',
        name: 'formAdviesIsolatieplanInterest',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormIsolatieplanInterest.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'advies-end',
        name: 'adviesEnd',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/AdviesEndView.vue'
          ),
        meta: {
          analyticsViewType: 'completed',
        },
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'cavity-wall',
        name: 'formCavityWall',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormCavityWallView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'cavity-wall-paint',
        name: 'formCavityWallPaint',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormCavityWallPaintView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'cavity-wall-stucco',
        name: 'formCavityWallStucco',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormCavityWallStuccoView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'floor',
        name: 'formFloor',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormFloorView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'floor-details',
        name: 'formFloorDetails',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormFloorDetailsView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'roof',
        name: 'formRoof',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormRoofView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'roof-attic',
        name: 'formRoofAttic',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormRoofAtticView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'roof-finishing',
        name: 'formRoofFinishing',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormRoofFinishingView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'glazing',
        name: 'formGlazing',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormGlazingView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'glazing-frame',
        name: 'formGlazingFrame',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormGlazingFrameView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'glazing-dimensions',
        name: 'formGlazingDimensions',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormGlazingDimensions.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'heatpump',
        name: 'formHeatpump',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormHeatpumpView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'solar',
        name: 'formSolar',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/FormSolarView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'blocking-issue',
        name: 'blockingGlobalIssue',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/QualificationGlobalView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
      {
        path: 'result',
        name: 'qualificationResult',
        component: () =>
          import(
            /* webpackChunkName: "formViews" */ '../views/QualificationView.vue'
          ),
        beforeEnter: [QualGuard, FlowGuard],
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    // Scroll always to top on navigation
    return { top: 0 };
  }
})

router.beforeEach((to) => {
  let uiStore = useInterfaceStore();
  let lead = useLeadStore();
  let fetcherViewName = 'fetcher';
  // set route on uiStore
  uiStore.currentRoute = to;
  // if url contains partner coupon, set on store
  if (to.query.coupon && !uiStore.skipFormPartnerCoupon) {
    uiStore.setPartnerCouponOnLoad(to.query.coupon);
    to.query.coupon = null;
  }
  // if url contains internal user ID, set on stores
  if (to.query.sf_user_id) {
    uiStore.setInternalUserIdOnLoad(to.query.sf_user_id);
  }
  // if url contains load_dummy_data, run fetchDummyData
  if (to.query.load_dummy_data) {
    LeadDataService.fetchDummyData();
    delete to.query.load_dummy_data;
  }
  if (hasCookieConsentQuery(to.query)) {
    const cookiesAccepted = getCookieConsentQueryValue(to.query);
    setCookieConsent(cookiesAccepted);
    lead.setCookiesAccepted(cookiesAccepted);
    GoogleAnalyticsService.syncWithConsent(cookiesAccepted);
    FacebookPixelService.syncWithConsent(cookiesAccepted);
    if (to.name !== fetcherViewName) {
      delete to.query.cookies_accepted;
    }
  } else if (hasCookieConsentDecision()) {
    const cookiesAccepted = getCookieConsentValue();
    lead.setCookiesAccepted(cookiesAccepted);
    GoogleAnalyticsService.syncWithConsent(cookiesAccepted);
    FacebookPixelService.syncWithConsent(cookiesAccepted);
  }
  // if data was not already fetched and path contains uuid (and it is not the fetcher view, to avoid infinite redirect loop)
  if (!uiStore.fetchedFromSf && !uiStore.fetchedFromSession && to.params.uuid && to.name !== fetcherViewName) {
    uiStore.setUuid(to.params.uuid);
    // redirect to loading page
    return {
      name: fetcherViewName,
      params: to.params,
      query: {...to.query, ...{redir: to.name}}
    };
  }

  // else load query params for lead
  // lead source param
  if (to.query.src) {
    lead.setLeadSource(to.query.src);
    if (to.name !== fetcherViewName) {
      delete to.query.src;
    }
  }
  // show hidden interests param
  if (to.query.all_intr) {
    lead.meta.AllInterestsEnabled = to.query.all_intr !== 'false';
    if (to.name !== fetcherViewName) {
      delete to.query.all_intr;
    }
  }

  // disableCouponForm
  if (to.query.disable_coupon) {
    uiStore.disableCouponForm = to.query.disable_coupon !== 'false';
    if (to.name !== fetcherViewName) {
      delete to.query.disable_coupon;
    }
  }

  // pre-load contact details
  lead.setContactDetailsFromQueryParams(to.query);

  // if skip_contact_details
  if (to.query.skip_contact_details && to.query.skip_contact_details !== 'false') {
    return {
      name: 'formInterests',
      params: to.params,
      query: {...StripQuery(to.query, 'skip_contact_details')}
    };
  }
  // if specific interest, ensure interest is checked and redirect
  if (to.query.check_interest && to.name !== fetcherViewName) {
    let interestDefinition = getInterestDefinition(to.query.check_interest, true);
    if (interestDefinition) {
      const lead = useLeadStore();
      lead.checkInterest(interestDefinition.value);
      return {
        name: interestDefinition.viewName,
        params: to.params,
        query: {...StripQuery(to.query, 'check_interest')}
      };
    }
  }
});

router.beforeEach((to) => {
  if (!to.meta?.analyticsViewType || !BasicAnalyticsService.analyticsEnabled()) {
    return;
  }

  BasicAnalyticsService.submitFromCurrentUrl('GET', to.meta.analyticsViewType).catch((error) => {
    console.error(error);
  });
});

router.afterEach(debounce( () => {
  let uiStore = useInterfaceStore();
  (async () => {
    LeadDataService.pushRemoteData(uiStore.uuid);
  })().catch(() => {
    console.log('Push cancelled.');
  });
}, 4000, {
  'leading': true,
  'trailing': true,
}));

export default router
